// Tool-call activity strip for the chat assistant bubble.
//
// Renders a horizontal row of chips, one per tool the model invoked
// this turn:
//   [⟳ search_kb] [✓ ask_question 1.2s] [✗ fetch_url failed]
//
// Chips animate in with a slide+fade, then transition through:
//   running -> done (or error)
//
// State is owned by the parent (app.jsx) on the message itself
// (msg.toolEvents). Persisted with the message so past assistant
// turns also show their tool history when scrolled back to.
//
// Also hosts the message-bubble fade-up keyframe -- both this strip
// and the fade-up are "chat animation" polish so they share one
// CSS-injecting module instead of scattering keyframes across files.
//
// Follows the ThinkingIndicator.jsx pattern: a one-time <style id="...">
// element so HMR / re-mounts don't stack duplicates.

import { memo } from "react";

const STYLE_ID = "tool-activity-strip-style";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
/* Tool chip entrance: slight upward slide + scale-in. cubic-bezier
   is the "out" curve from the Material spec; feels physical without
   being bouncy. */
@keyframes cf-tool-chip-enter { from { opacity: 0; transform: translateY(6px) scale(0.92); } to { opacity: 1; transform: translateY(0) scale(1); } }
/* Spinner: rotates inside the chip while status === "running" */
@keyframes cf-tool-chip-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
/* Message bubble entrance: fade + slight rise so new messages don't
   pop in jarringly. Runs once per element instantiation (React keys
   keep DOM nodes stable, so this only fires for genuinely new
   messages, not re-renders of existing ones). */
@keyframes cf-msg-enter { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
[data-cf-msg-enter] { animation: cf-msg-enter 180ms ease-out both; }

.cf-tool-strip { display: flex; flex-wrap: wrap; gap: 6px; margin: 2px 0 10px; }
.cf-tool-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 9px 3px 7px;
  border-radius: 12px;
  font-size: 11px; font-family: monospace; line-height: 1;
  animation: cf-tool-chip-enter 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
  white-space: nowrap;
}
.cf-tool-chip[data-status="running"] { background: rgba(91,163,201,0.15); color: #5ba3c9; border: 1px solid rgba(91,163,201,0.3); }
.cf-tool-chip[data-status="done"]    { background: rgba(34,197,94,0.12);  color: #22c55e; border: 1px solid rgba(34,197,94,0.28); }
.cf-tool-chip[data-status="error"]   { background: rgba(239,68,68,0.12);  color: #ef4444; border: 1px solid rgba(239,68,68,0.28); }

.cf-tool-spinner {
  width: 9px; height: 9px;
  border: 1.5px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: cf-tool-chip-spin 0.8s linear infinite;
  flex-shrink: 0;
}

.cf-tool-icon-ok, .cf-tool-icon-err { font-weight: 700; }
.cf-tool-duration { opacity: 0.62; }

/* Respect prefers-reduced-motion: drop the entrance and spinner
   animations to a static state. WCAG 2.3.3 recommendation -- some
   users get motion-sick from gratuitous animation. */
@media (prefers-reduced-motion: reduce) {
  [data-cf-msg-enter] { animation: none; }
  .cf-tool-chip { animation: none; }
  .cf-tool-spinner { animation: none; border-top-color: currentColor; opacity: 0.5; }
}
`;
  document.head.appendChild(s);
}

// Pretty-print a tool name for the chip: strip the mcp__server__ prefix
// (keep only the actual tool name) so the chip stays narrow. Falls back
// to the raw name for built-in tools (search_kb / web_search etc.).
function fmtName(name) {
  if (typeof name !== "string" || !name) return "?";
  if (name.startsWith("mcp__")) {
    const parts = name.split("__");
    if (parts.length >= 3) return parts.slice(2).join("__");
  }
  return name;
}
function fmtMs(startedAt, endedAt) {
  if (!startedAt || !endedAt) return null;
  const d = endedAt - startedAt;
  if (d < 0) return null;
  if (d < 1000) return d + "ms";
  return (d / 1000).toFixed(1) + "s";
}

export const ToolActivityStrip = memo(function ToolActivityStrip({ events }) {
  if (!Array.isArray(events) || events.length === 0) return null;
  return (
    <div className="cf-tool-strip" role="status" aria-live="polite" aria-label="Tool calls">
      {events.map((ev, i) => {
        const status = ev.status || "running";
        const dur = status === "done" || status === "error" ? fmtMs(ev.startedAt, ev.endedAt) : null;
        const title = status === "error" && ev.error
          ? `${ev.name} failed: ${ev.error}`
          : ev.name;
        return (
          <span key={i} className="cf-tool-chip" data-status={status} title={title}>
            {status === "running" && <span className="cf-tool-spinner" aria-hidden="true" />}
            {status === "done"    && <span className="cf-tool-icon-ok"  aria-hidden="true">✓</span>}
            {status === "error"   && <span className="cf-tool-icon-err" aria-hidden="true">✗</span>}
            <span>{fmtName(ev.name)}</span>
            {dur && <span className="cf-tool-duration">{dur}</span>}
          </span>
        );
      })}
    </div>
  );
});

export default ToolActivityStrip;
